import { Controller } from "@hotwired/stimulus";
import * as ZXing from "@zxing/library";
import * as Turbo from "@hotwired/turbo";
import { cookie, fetchWithCSRF, getHeaderWithCSRF } from "../utils";

const COOKIE_SCAN_ID_KEY = "scan_selected_device_id";

export default class extends Controller {
  /**
   * # Scanner Controller.
   *
   * ## Required API
   * __Targets__
   *  * scanUi - where the scanner will be displayed
   *  * sourceSelect - Video source selection (filled by controller)
   *
   * __Values__
   * * formUrl - url to post 'wattro_id' to. Controller will render Stream or navigate to result.
   */
  static targets = ["scanUi", "sourceSelect"];
  static values = {
    formUrl: String,
    mounted: Boolean,
  };

  async connect() {
    if (this.isAlreadyConnected()) return;
    try {
      this.verifySetup();
    } catch (e) {
      this.errToUser("Starten des Scanner Controllers fehlgeschlagen.", e);
      return;
    }
    try {
      await this.setupScanner();
    } catch (e) {
      this.errToUser("Laden des Scanners fehlgeschlagen.", e);
      return;
    }
    try {
      await this.scanFromSelectedCamOrDefault();
    } catch (e) {
      this.errToUser("Starten des Scanners fehlgeschlagen.", e);
      return;
    }
    await this.populateSourceSelect();
  }

  disconnect() {
    this.resetScanner();
  }

  isAlreadyConnected() {
    if (this.hasMountedValue && this.mountedValue) {
      return true;
    }
    this.mountedValue = true;
    return false;
  }

  resetScanner() {
    this.currentlyHandlingText = undefined;
    if (!this.scanner) return;
    this.scanner.reset();
    delete this.scanner;
  }

  verifySetup() {
    const basicApi = {
      scanUiTarget: this.hasScanUiTarget,
      sourceSelectTarget: this.hasSourceSelectTarget,
      formUrlValue: this.hasFormUrlValue,
    };
    if (Object.values(basicApi).some((has) => !has)) {
      throw new Error(`Required basic API not met ${JSON.stringify(basicApi)}`);
    }
    getHeaderWithCSRF(); // throws if no csrf token set.
  }

  async setupScanner() {
    this.currentlyHandlingText = undefined;
    if (this.scanner) {
      await this.resetScanner();
    }
    const hints = new Map();
    // see https://github.com/zxing-js/library/blob/master/src/core/MultiFormatReader.ts#L101 on how hints are processed
    hints.set(ZXing.DecodeHintType.POSSIBLE_FORMATS, [
      ZXing.BarcodeFormat.QR_CODE,
      ZXing.BarcodeFormat.DATA_MATRIX,
    ]);
    this.scanner = new ZXing.BrowserMultiFormatReader(
      hints /*timeout after successful scan defaults to 500ms */,
    );
    this.selectedDeviceId = cookie.get(COOKIE_SCAN_ID_KEY);
    console.debug("got cookie ", this.selectedDeviceId);
  }

  async scanFromSelectedCamOrDefault() {
    if (!this.scanner) {
      await this.setupScanner();
    } else {
      this.scanner.reset();
    }
    if (this.selectedDeviceId && this.selectedDeviceId !== "") {
      console.debug("setting device to id ", this.selectedDeviceId);
      this.scanner.decodeFromVideoDevice(
        this.selectedDeviceId,
        this.scanUiTarget,
        this.handleScan.bind(this),
      );
    } else {
      // try to start from the environment facing cam
      this.scanner.decodeFromConstraints(
        { video: { facingMode: "environment" } },
        this.scanUiTarget,
        this.handleScan.bind(this),
      );
    }
  }

  async populateSourceSelect() {
    if (!this.scanner) {
      await this.setupScanner();
    }
    const videoInputDevices = await this.scanner.listVideoInputDevices();

    if (videoInputDevices.length == 1) {
      const element = videoInputDevices[0];
      this.sourceSelectTarget.disabled = true;
      const sourceOption = document.createElement("option");
      sourceOption.text = `(default) ${element.label}`;
      sourceOption.value = "";
      this.sourceSelectTarget.appendChild(sourceOption);
    } else {
      const sourceOption = document.createElement("option");
      sourceOption.text = "default";
      sourceOption.value = "";

      this.sourceSelectTarget.appendChild(sourceOption);
      for (const element of videoInputDevices) {
        const deviceId = element.deviceId;
        const sourceOption = document.createElement("option");
        sourceOption.text = element.label;
        sourceOption.value = deviceId;
        this.sourceSelectTarget.appendChild(sourceOption);
      }
    }

    this.sourceSelectTarget.onchange = () => {
      this.selectedDeviceId = this.sourceSelectTarget.value;
      cookie.set(COOKIE_SCAN_ID_KEY, this.selectedDeviceId);
      console.debug("set cookie to ", cookie.get(COOKIE_SCAN_ID_KEY));
      this.scanFromSelectedCamOrDefault();
    };
  }

  handleScan(result, error) {
    if (result) this.handleSuccessfulScan(result.text, result);
    if (error) this.handleScanErr(error.message, error);
  }

  handleScanErr(errorMessage /*: string */, error /*: ZXing Exception */) {
    if (error instanceof ZXing.NotFoundException) {
      return;
    }
    try {
      this.errToUser(`Scanner Fehler: ${errorMessage}`, error);
    } catch (e) {
      console.error(e);
      console.warn(error);
    }
  }

  handleSuccessfulScan(decodedText, result) {
    if (this.currentlyHandlingText === decodedText) return; //early exit if we're already handling this value
    this.currentlyHandlingText = decodedText;
    try {
      if (!decodedText || !decodedText.startsWith("wattro")) {
        const url = `/ui/asset/?general_search=${encodeURIComponent(
          decodedText,
        )}`;
        this.errToUser(
          `Kein Wattro Label <a href='${url}' class="text-reset fw-bold">'${decodedText}' suchen</a>.`,
          result,
        );
        return;
      }
      const wattro_id = decodedText.split("/").pop();
      this.infoToUser(`${wattro_id} erkannt.`);
      this.postToForm(wattro_id);
    } catch (e) {
      this.errToUser(`Fehler beim Verarbeiten von '${decodedText}'.`, e);
    }
  }

  async postToForm(wattro_id) {
    const formData = new FormData();
    formData.append("wattro_id", wattro_id);
    const response = await fetchWithCSRF(this.formUrlValue, {
      method: "POST",
      body: formData,
    });
    if (!response.status >= 500) {
      throw response;
    }
    const contentType = response.headers.get("Content-Type") ?? "unset";
    const is_stream = contentType.startsWith("text/vnd.turbo-stream");
    const html = await response.text();
    if (is_stream) {
      Turbo.renderStreamMessage(html);
    } else {
      this.resetScanner();
      await history.pushState({ wattro_id }, "", response.url);
      document.querySelector("html").innerHTML = html;
    }
    this.currentlyHandlingText = undefined;
  }

  errToUser(text, console_data) {
    console.log(text);
    if (console_data) console.log(console_data);

    const newError = document.createElement("div");
    newError.classList = "toast show text-white bg-danger";
    newError.innerHTML = `<div class="toast-header">
            <strong class="me-auto">Scanner Fehler</strong>
            <button type="button"
              class="btn-close"
              data-bs-dismiss="toast"
              aria-label="Close"></button>
              </div>
            <div class="toast-body">${text}</div>`;
    const toastWrapper = document.getElementById("toast_stream");
    toastWrapper.replaceChildren(newError);
  }

  infoToUser(text, console_data) {
    console.log(text);
    if (console_data) console.log(console_data);

    const infoToast = document.createElement("div");
    infoToast.classList = "toast show";
    infoToast.innerHTML = `<div class="toast-header"><span class="me-auto">${text}</span>
            <button type="button"
              class="btn-close"
              data-bs-dismiss="toast"
              aria-label="Close"></button>
            </div>`;
    const toastWrapper = document.getElementById("toast_stream");
    toastWrapper.insertAdjacentElement("beforeend", infoToast);
  }
}
